// Comprehensive mappings for dress attributes to human-readable descriptions
// Used for generating SEO-friendly descriptions and structured data

export const DRESS_ATTRIBUTE_DESCRIPTIONS = {
  // Length descriptions - matching the format from dress-style-options.ts
  length: {
    mini: "Mini - Above the knee",
    knee: "Knee - At the knee",
    calf: "Calf - At the mid-calf",
    ankle: "Ankle - At the ankle",
    floor: "Floor - Just reaches the floor",
    floor_length: "Floor - Just reaches the floor",
    tea_length: "Tea - Mid-calf length",
    high_low: "High-Low - Asymmetrical hemline",
    chapel: "Chapel - Slight train (1-2 feet past feet)",
    cathedral: "Cathedral - Long train (2-3 feet+)",
    royal: "Royal - Very long, dramatic train",
    train1: "Chapel - Slight train (1-2 feet past feet)",
    train2: "Cathedral - Long train (2-3 feet+)",
    train3: "Royal - Very long, dramatic train",
  },

  // Sleeve descriptions
  sleeve: {
    sleeveless: "Sleeveless - No sleeves",
    short: "Short - Above the elbow",
    short_sleeve: "Short - Above the elbow",
    elbow: "Elbow - At the elbow",
    three_quarter: "Three-Quarter - Below the elbow",
    threequarter: "Three-Quarter - Below the elbow",
    long: "Long - Full length",
    long_sleeve: "Long - Full length",
    bell: "Bell - Flare out from elbow",
    cutouts: "Cutouts - Open shoulders",
    asymmetric: "Asymmetric - Remove the left or right sleeve",
    off_shoulder: "Off-Shoulder - Sits below shoulder line",
    one_shoulder: "One-Shoulder - Single shoulder strap",
  },

  // Neckline descriptions
  neckline: {
    off_shoulder: "Off-Shoulder - Sits below shoulder line",
    plunge: "Plunge - Deep dramatic V-neck",
    v: "V - Classic V-neck opening",
    v_neck: "V - Classic V-neck opening",
    heart: "Heart-Shaped - Curved neckline",
    heart_shaped: "Heart-Shaped - Curved neckline",
    sweetheart: "Heart-Shaped - Curved neckline",
    scoop: "Scoop - Curved U-shaped neckline",
    draped: "Draped - Soft, flowing fabric draping",
    boat: "Boat - Wide opening to shoulders",
    straight: "Straight - Horizontal, across chest",
    collared: "Collared - Like a dress shirt",
    halter: "Halter - Covers neck and chest, exposes back",
    high: "High - Conservative, covers neck, chest, and back",
    one_shoulder: "One-Shoulder - Single shoulder strap",
  },

  // Waistline descriptions
  waistline: {
    natural: "Natural - At the natural waistline",
    empire: "Empire - Just under the bust",
    dropped: "Dropped - Below the natural waist",
    high_waist: "High-Waist - Above the natural waist",
    low_waist: "Low-Waist - Below the natural waist",
  },

  // Silhouette/Fit descriptions
  silhouette: {
    fitted: "Fitted - Tight on body",
    mermaid: "Mermaid - Fitted down to the knees, then flares dramatically",
    trumpet: "Trumpet - Fitted down to the mid-thigh, then flares dramatically",
    drop_waist:
      "Drop-Waist - Fitted down to the waist/hips, then flares dramatically",
    dropwaist:
      "Drop-Waist - Fitted down to the waist/hips, then flares dramatically",
    a_line:
      "A-Line - Fitted down to the belly button, then flares dramatically",
    aline: "A-Line - Fitted down to the belly button, then flares dramatically",
    empire: "Empire - Fitted just under the bust, then flares dramatically",
    sheath: "Sheath - Straight fit, body-hugging column shape",
    fit_flare:
      "Fit & Flare - Fitted through the waist and hips, then gently flares out",
    ball_gown:
      "Ball Gown - Fitted bodice with a voluminous skirt for a dramatic effect",
    ballgown:
      "Ball Gown - Fitted bodice with a voluminous skirt for a dramatic effect",
  },

  // Fabric/Texture descriptions
  fabric: {
    cotton: "Cotton Blend - Breathable, budget-friendly everyday fabric",
    silk: "Silk Charmeuse - Lustrous, lightweight silk with a smooth drape",
    satin:
      "Duchess Satin - Heavyweight satin with a luxurious sheen and structure",
    mikado: "Mikado - Crisp silk blend with a subtle sheen and structured hand",
    organza:
      "Organza - Sheer, crisp fabric often used for full skirts and overlays",
    tulle: "Tulle - Fine netting for volume, layering, and delicate details",
    velvet: "Velvet - Soft, pile fabric with a rich depth and warmth",
    crepe:
      "Crepe de Chine - Lightweight silk with a matte, slightly textured surface",
    crepe_de_chine:
      "Crepe de Chine - Lightweight silk with a matte, slightly textured surface",
    dupioni:
      "Dupioni Silk - Crisp raw silk with irregular slub texture for depth",
    dupioni_silk:
      "Dupioni Silk - Crisp raw silk with irregular slub texture for depth",
    chiffon: "Chiffon - Flowing, lightweight, semi-transparent",
    lace: "Lace - Intricate openwork patterns in various weights and designs",
    beaded:
      "Beaded Embroidery - Embellished with beads and pearls for texture and shimmer",
    beaded_embroidery:
      "Beaded Embroidery - Embellished with beads and pearls for texture and shimmer",
    sequin:
      "Sequin Embellishment - Covered with sequins for high-impact sparkle",
    sequin_embellishment:
      "Sequin Embellishment - Covered with sequins for high-impact sparkle",
    plain: "Plain - Simple, unadorned fabric",
  },
} as const;

// Helper function to get description for any attribute
export function getAttributeDescription(
  category: keyof typeof DRESS_ATTRIBUTE_DESCRIPTIONS,
  value: string | null
): string | null {
  if (!value) return null;

  const normalizedValue = value.toLowerCase().replace(/[_-]/g, "_");
  const descriptions = DRESS_ATTRIBUTE_DESCRIPTIONS[category];

  // Try exact match first
  if (normalizedValue in descriptions) {
    return descriptions[normalizedValue as keyof typeof descriptions];
  }

  // Try partial match for compound values
  for (const [key, description] of Object.entries(descriptions)) {
    if (normalizedValue.includes(key) || key.includes(normalizedValue)) {
      return description;
    }
  }

  // Fallback to formatted version of the original value
  return value.replace(/[_-]/g, " ").replace(/\b\w/g, l => l.toUpperCase());
}

// Helper function to get short description (just the main part, no explanation)
export function getShortAttributeDescription(
  category: keyof typeof DRESS_ATTRIBUTE_DESCRIPTIONS,
  value: string | null
): string | null {
  const fullDescription = getAttributeDescription(category, value);
  if (!fullDescription) return null;

  // Extract the main part before the dash
  const mainPart = fullDescription.split(" - ")[0];
  return mainPart;
}

// Dress type to Google product category mapping
export const DRESS_TYPE_CATEGORIES = {
  wedding:
    "Apparel & Accessories > Clothing > Wedding & Bridal Party > Wedding Dresses",
  prom: "Apparel & Accessories > Clothing > Formal Wear > Prom Dresses",
  bridesmaid:
    "Apparel & Accessories > Clothing > Wedding & Bridal Party > Bridesmaid Dresses",
  quinceanera:
    "Apparel & Accessories > Clothing > Formal Wear > Quinceanera Dresses",
  formal: "Apparel & Accessories > Clothing > Formal Wear > Evening Gowns",
  custom: "Apparel & Accessories > Clothing > Dresses",
} as const;

// Helper to get Google product category for dress type
export function getDressCategory(dressType: string): string {
  // Fold "ñ"→"n" so legacy "quinceañera" dress-type values still resolve.
  const normalizedType = dressType.toLowerCase().replace(/ñ/g, "n");
  return (
    DRESS_TYPE_CATEGORIES[
      normalizedType as keyof typeof DRESS_TYPE_CATEGORIES
    ] || DRESS_TYPE_CATEGORIES.custom
  );
}
